import { useAuth0 } from "@auth0/auth0-react";
import { useMemo } from "react";
import type * as React from "react";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { Box, Divider, Link, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import ArrowTargetBecomeMemberIcon from "assets/images/arrow-target-become-member.svg";
import GLLogo from "assets/images/GL-Logo.svg";
import GiftMembershipStepPencilIcon from "assets/images/gift-membership-step-pencil.svg";
import LogoIconDefault from "assets/images/LogoIconDefault.svg";
import UnpaidInviteAccentLine from "assets/images/unpaid-invite-welcome-accent-line.svg";
import UnpaidInviteEmailExample from "assets/images/unpaid-invite-welcome-email-example.png";
import TeamHandsImage from "assets/images/unpaid-invite-welcome-team-hands.png";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import GLButton from "components/v3/sharedComponents/GLButton";
import HorizontalHowItWorksSteps, {
  horizontalHowItWorksImgIcon
} from "components/v3/sharedComponents/HorizontalHowItWorksSteps";
import Video from "components/v3/sharedComponents/Video";
import { COLORS } from "components/v3/Theme/colors";
import { GENOMIC_LIFE_PUBLIC_MARKETING_HOME_URL } from "constants/externalPathConstants";
import GenomicLifeLegalDisclaimer from "components/v3/sharedComponents/GenomicLifeLegalDisclaimer";
import PageTitle from "utils/PageTitle";

const NAVIGATION_MAILTO = "mailto:navigation@genomiclife.com";

const publicPolicyLinkSx = {
  color: "inherit",
  textDecoration: "underline",
  textUnderlineOffset: "2px"
} as const;

const primaryCtaSx = {
  maxWidth: { sm: "none", xs: "80%" },
  width: { sm: "auto", xs: "80%" }
} as const;

const UnpaidInviteWelcomePage: React.FC = () => {
  const { loginWithRedirect } = useAuth0();
  const { t } = useTranslation();

  PageTitle("Welcome");

  const goRegistration = () => {
    loginWithRedirect({
      authorizationParams: {
        screen_hint: "signup"
      }
    });
  };

  const howItWorksSteps = useMemo(
    () => [
      {
        stepKey: "create",
        title: "Create your account",
        body: "Get started by setting up your account and personalizing your profile. You will need your invite code to complete this step.",
        icon: horizontalHowItWorksImgIcon(GiftMembershipStepPencilIcon, 24)
      },
      {
        stepKey: "member",
        title: "Become a member",
        body: "Purchase your Genomic Life membership and answer some questions regarding your health, lifestyle, and your relatives' health.",
        icon: horizontalHowItWorksImgIcon(ArrowTargetBecomeMemberIcon, 24)
      },
      {
        stepKey: "next",
        title: "Get personalized next steps",
        body: "We'll guide you to next steps. That could mean DNA screening, talking to our navigation team, or having a conversation with your doctor.",
        icon: horizontalHowItWorksImgIcon(LogoIconDefault, 24)
      }
    ],
    []
  );

  return (
    <GradientFramedLayout
      makeLogoClickable={false}
      showFooter={false}
      showHeader={false}
    >
      <Box
        sx={{
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: { md: 10, xs: 8 },
          marginX: "auto",
          maxWidth: 1280,
          minWidth: 0,
          pb: { md: 12, xs: 8 },
          pt: { md: 10, xs: 6 },
          px: { md: 4, xs: 2 },
          width: "100%"
        }}
      >
        <Box
          component="section"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            width: "100%"
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              gap: 3,
              marginX: "auto",
              maxWidth: { md: 1140, xs: "100%" },
              textAlign: "center",
              width: "100%",
              mb: { md: 10, xs: 3 }
            }}
          >
            <Box
              alt={String(t("LOGO_ALT_TEXT"))}
              component="img"
              src={GLLogo}
              sx={{ display: "block", height: 44, width: 185 }}
            />
            <Box sx={{ position: "relative", width: "100%" }}>
              <Typography
                component="h1"
                sx={{
                  display: "block",
                  typography: { md: "h2_black", xs: "h3_black" },
                  mt: 4,
                  mb: 0
                }}
              >
                Unlock healthcare
              </Typography>
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "column",
                  mt: 0,
                  position: "relative",
                  width: "100%"
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    background: `linear-gradient(to right, ${COLORS.ACCENT3}, ${COLORS.PRIMARY})`,
                    backgroundClip: "text",
                    display: "inline-block",
                    fontStyle: "italic",
                    typography: { md: "h2_serif_black", xs: "h3_serif_black" },
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    zIndex: 1
                  }}
                >
                  tailored to you.
                </Typography>
                <Box
                  alt=""
                  aria-hidden
                  component="img"
                  src={UnpaidInviteAccentLine}
                  sx={{
                    display: "block",
                    height: { md: 25, xs: 10 },
                    marginTop: { md: -2.2, xs: -0.5 },
                    maxWidth: "100%",
                    objectFit: "contain",
                    objectPosition: "center",
                    width: "100%",
                    zIndex: 0
                  }}
                />
              </Box>
            </Box>
            <GLButton
              buttonType="primary"
              onClick={goRegistration}
              rightIcon={<ArrowForward />}
              text="Create account & purchase plan"
              sx={primaryCtaSx}
            />
          </Box>
        </Box>

        <Box
          component="section"
          sx={{
            alignItems: "stretch",
            display: { xs: "none", lg: "flex" },
            flexDirection: "row",
            gap: 7
          }}
        >
          <Box
            sx={{
              display: "flex",
              flex: 1,
              flexDirection: "column",
              gap: 3,
              minWidth: 0,
              textAlign: "left"
            }}
          >
            <Typography
              component="h2"
              sx={{
                typography: { md: "h4_dark_gray_2", xs: "h5_dark_gray_2" }
              }}
            >
              Most people react to their health. Here&apos;s your chance to get
              ahead of it.
            </Typography>
            <Typography component="p" variant="body_dark_gray" sx={{ m: 0 }}>
              A Genomic Life membership unlocks healthcare tailored to you,
              bridging the gap between what&apos;s available in healthcare today
              and what&apos;s truly personalized. Because your health matters
              too much to leave it to chance.
            </Typography>
            <Link
              href={GENOMIC_LIFE_PUBLIC_MARKETING_HOME_URL}
              rel="noopener noreferrer"
              target="_blank"
              underline="always"
              sx={{ typography: "body_dark_gray" }}
            >
              Learn more about Genomic Life
            </Link>
          </Box>
          <Box
            sx={{
              borderRadius: "4px 32px 32px 120px",
              flex: "0 0 46%",
              lineHeight: 0,
              maxHeight: 420,
              overflow: "hidden",
              position: "relative",
              width: "46%"
            }}
          >
            <Box
              alt=""
              component="img"
              src={TeamHandsImage}
              sx={{
                display: "block",
                height: "auto",
                maxHeight: 420,
                objectFit: "cover",
                transform: "translateY(70px) translateX(70px) scale(1.5)",
                transformOrigin: "center center",
                width: "100%"
              }}
            />
            <Box
              aria-hidden
              sx={{
                backgroundColor: alpha(COLORS.PRIMARY_DARKEST, 0.41),
                borderRadius: "4px 32px 32px 120px",
                inset: 0,
                pointerEvents: "none",
                position: "absolute"
              }}
            />
          </Box>
        </Box>

        <HorizontalHowItWorksSteps
          sectionSubtitle="It only takes a few minutes to get started."
          sectionTitle="Getting started is as easy as 1-2-3"
          steps={howItWorksSteps}
        />

        <Box
          component="section"
          sx={{
            alignItems: { lg: "flex-start", xs: "stretch" },
            display: "flex",
            flexDirection: { lg: "row", xs: "column" },
            gap: { lg: 8, md: 6, xs: 4 }
          }}
        >
          <Box
            sx={{
              display: "flex",
              flex: { lg: "1 1 0", xs: "none" },
              flexDirection: "column",
              gap: 3,
              minWidth: 0
            }}
          >
            <Typography component="h2" variant="h4_black">
              Do you have an invite code?
            </Typography>
            <Typography component="p" variant="body_dark_gray" sx={{ m: 0 }}>
              Check your inbox for an email from us. Don&apos;t see it? Check
              your spam folder. You can also contact our team for assistance.
            </Typography>
            <Typography component="p" variant="body_sm_dark_gray" sx={{ m: 0 }}>
              The email was sent by noreply@genomiclife-mail.com
            </Typography>
            <GLButton
              buttonType="primary"
              onClick={goRegistration}
              rightIcon={<ArrowForward />}
              text="Create account & purchase plan"
              sx={{
                mt: 4,
                alignSelf: { xs: "stretch", md: "center", lg: "flex-start" },
                maxWidth: { lg: "100%", md: "60%", xs: "100%" },
                width: { lg: "100%", md: "60%", xs: "100%" }
              }}
            />
          </Box>

          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              flex: { lg: "0 1 48%", xs: "none" },
              justifyContent: { lg: "flex-end", xs: "center" },
              minWidth: 0,
              width: { lg: "auto", xs: "100%" }
            }}
          >
            <Box
              alt="Example invitation email showing where to find your personal invite code"
              component="img"
              src={UnpaidInviteEmailExample}
              sx={{
                borderRadius: "16px",
                display: "block",
                height: "auto",
                maxWidth: { lg: "100%", xs: "min(100%, 400px)" },
                objectFit: "contain",
                width: { lg: "100%", xs: "auto" }
              }}
            />
          </Box>
        </Box>

        <Box
          component="section"
          sx={{
            bgcolor: COLORS.BACKGROUND_BLUE,
            borderRadius: "24px",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            p: { md: 6, xs: 3 }
          }}
        >
          <Typography component="h2" variant="h4_black">
            Genomic Life Complete
          </Typography>
          <Typography
            component="p"
            variant="body_lg"
            sx={{ m: 0, maxWidth: 880, mb: 4 }}
          >
            From DNA screenings to 1-on-1 support, education and navigation, we
            help members find a path forward that&apos;s tailored to their
            individual needs.
          </Typography>
          <Box
            sx={{
              borderRadius: "16px",
              boxShadow:
                "0px 10px 15px -3px rgba(0, 0, 0, 0.10), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)",
              overflow: "hidden",
              width: "100%",
              mb: 4
            }}
          >
            <Video
              height="100%"
              muted
              playing
              style={{
                aspectRatio: "16/9",
                height: "100%",
                width: "100%"
              }}
              width="100%"
              url="https://vimeo.com/896232421"
            />
          </Box>
          <Typography
            component="p"
            variant="body_strong_black"
            sx={{ m: 0, mb: 2 }}
          >
            We are here for you!
          </Typography>
          <Typography
            component="p"
            variant="body"
            sx={{ m: 0, maxWidth: 880, mb: 2 }}
          >
            Our team is dedicated to ensuring you have everything you need to
            unlock healthcare that&apos;s tailored to you. That starts right
            now. If you have any questions before signing up, simply{" "}
            <Box
              component="a"
              href={NAVIGATION_MAILTO}
              sx={{ ...publicPolicyLinkSx, fontWeight: 400 }}
            >
              contact our team
            </Box>
            .
          </Typography>
          <GLButton
            buttonType="primary"
            onClick={goRegistration}
            rightIcon={<ArrowForward />}
            text="Create account & purchase plan"
            sx={{
              mb: 2,
              maxWidth: { sm: "450px", xs: "100%" }
            }}
          />
          <Link
            href={GENOMIC_LIFE_PUBLIC_MARKETING_HOME_URL}
            rel="noopener noreferrer"
            target="_blank"
            underline="always"
            sx={{
              textDecorationColor: COLORS.BLACK,
              typography: "body_strong_black"
            }}
          >
            Learn more about Genomic Life Complete
          </Link>
        </Box>

        <Divider
          sx={{
            my: { sm: -6, xs: -3 },
            borderBottomWidth: "3px",
            borderColor: alpha(COLORS.PRIMARY_LIGHT, 0.2)
          }}
        />

        <GenomicLifeLegalDisclaimer
          component="footer"
          policyLinks="public"
          sx={{
            mx: "auto",
            pb: 4,
            textAlign: "left"
          }}
        />
      </Box>
    </GradientFramedLayout>
  );
};

export default UnpaidInviteWelcomePage;
